import type { Key, ReactNode } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { MembersManagementTableState, MembersTableColumn } from './types';

interface MembersManagementTableProps<Row> {
  readonly columns: readonly MembersTableColumn<Row>[];
  readonly rows: readonly Row[];
  readonly getRowKey: (row: Row) => Key;
  readonly getRowTestId?: (row: Row) => string;
  readonly state: MembersManagementTableState;
  readonly skeletonRowCount?: number;
  readonly renderActions?: (row: Row) => ReactNode;
  readonly renderActionsSkeleton?: () => ReactNode;
  readonly actionsHeader?: ReactNode;
}

export function MembersManagementTable<Row>({
  columns,
  rows,
  getRowKey,
  getRowTestId,
  state,
  skeletonRowCount = 4,
  renderActions,
  renderActionsSkeleton,
  actionsHeader = 'Actions',
}: MembersManagementTableProps<Row>) {
  const hasActions = renderActions !== undefined;
  const columnCount = columns.length + (hasActions ? 1 : 0);
  const showSkeleton = state.status === 'loading' && state.showSkeleton;
  const showRows = state.status === 'success' || state.status === 'refreshing';
  const isBusy = state.status === 'loading' || state.status === 'refreshing';

  return (
    <Table
      aria-busy={isBusy}
      className="min-w-full border-separate border-spacing-0"
    >
      <TableHeader>
        <TableRow className="text-left text-sm text-muted-foreground hover:bg-transparent">
          {columns.map((column) => (
            <TableHead
              aria-sort={column.ariaSort}
              key={column.key}
              className={cn('h-auto px-6 py-5', column.headerClassName)}
            >
              {column.header}
            </TableHead>
          ))}
          {hasActions ? (
            <TableHead className="h-auto px-6 py-5 text-right text-sm font-medium text-foreground">
              {actionsHeader}
            </TableHead>
          ) : null}
        </TableRow>
      </TableHeader>

      <TableBody>
        {showSkeleton
          ? Array.from({ length: skeletonRowCount }, (_, index) => (
              <TableRow key={`skeleton-${index}`} className="hover:bg-transparent">
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    className={cn('border-t border-border px-6 py-5 whitespace-normal', column.cellClassName)}
                  >
                    {column.renderSkeleton?.() ?? <Skeleton className="h-5 w-32 rounded-full" />}
                  </TableCell>
                ))}
                {hasActions ? (
                  <TableCell className="border-t border-border px-6 py-5 whitespace-normal">
                    {renderActionsSkeleton?.() ?? <Skeleton className="ml-auto h-10 w-28 rounded-full" />}
                  </TableCell>
                ) : null}
              </TableRow>
            ))
          : showRows ? rows.map((row) => (
              <TableRow
                key={getRowKey(row)}
                data-testid={getRowTestId?.(row)}
                className="align-middle hover:bg-transparent"
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    className={cn('border-t border-border px-6 py-5 whitespace-normal', column.cellClassName)}
                  >
                    {column.renderCell(row)}
                  </TableCell>
                ))}
                {renderActions ? (
                  <TableCell className="border-t border-border px-6 py-5 whitespace-normal">
                    {renderActions(row)}
                  </TableCell>
                ) : null}
              </TableRow>
            )) : null}

        {state.status === 'error' || state.status === 'empty' ? (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={columnCount} className="border-t border-border p-0 whitespace-normal">
              {state.content}
            </TableCell>
          </TableRow>
        ) : null}
      </TableBody>
    </Table>
  );
}
